import { convertToCSV } from "./GeneralFuntions";

const EXPECTED_HEADER = [
    "sampleID",
    "coutryOrigin",
    "host",
    "isolationSource",
    "collectionDate",
    "authors",
];

function isEmptyCell(v) {
    return v === null || v === undefined || String(v).trim() === "";
}

function trimTrailingEmpty(arr = []) {
    let end = arr.length;
    while (end > 0 && isEmptyCell(arr[end - 1])) end--;
    return arr.slice(0, end);
}

function parseCSVLine(line) {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const next = line[i + 1];

        if (char === '"') {
            if (inQuotes && next === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === "," && !inQuotes) {
            result.push(current.trim());
            current = "";
        } else {
            current += char;
        }
    }

    result.push(current.trim());
    return result;
}

function parseCSV(text) {
    const lines = String(text ?? "")
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/);

    const parsed = lines.map((line) =>
        trimTrailingEmpty(parseCSVLine(line))
    );

    if (parsed.length === 0) {
        return { header: [], rows: [] };
    }

    const header = parsed[0];
    const rows = parsed.slice(1);

    return { header, rows };
}

function isRowCompletelyEmpty(row = []) {
    return !row.some((cell) => !isEmptyCell(cell));
}

function isInstructionRow(row = []) {
    const firstCell = String(row?.[0] ?? "").trim();
    return firstCell.startsWith("**");
}

function isValidSampleID(value) {
    const s = String(value ?? "").trim();
    if (!s) return false;

    return /^[A-Za-z0-9_-]+$/.test(s);
}

function isValidAuthors(value) {
    const s = String(value ?? "").trim();
    if (!s) return false;

    const parts = s.split(";").map((p) => p.trim());

    return parts.every((p) => p !== "");
}

function isValidDateDDMMYY(value) {
    const s = String(value ?? "").trim();

    const m = s.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
    if (!m) return false;

    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yy = Number(m[3]);

    if (mm < 1 || mm > 12) return false;
    if (dd < 1 || dd > 31) return false;

    const yyyy = 2000 + yy;
    const d = new Date(yyyy, mm - 1, dd);

    return (
        d.getFullYear() === yyyy &&
        d.getMonth() === mm - 1 &&
        d.getDate() === dd
    );
}

function headerMatchesStrictWithEmptyExtras(header, rows) {
    if (!Array.isArray(header)) return false;
    if (header.length < EXPECTED_HEADER.length) return false;

    for (let i = 0; i < EXPECTED_HEADER.length; i++) {
        if (String(header[i] ?? "").trim() !== EXPECTED_HEADER[i]) {
            return false;
        }
    }

    for (let col = EXPECTED_HEADER.length; col < header.length; col++) {
        const headerName = header[col];

        if (!isEmptyCell(headerName)) return false;

        const colHasData = rows.some((r) => !isEmptyCell(r?.[col]));

        if (colHasData) return false;
    }

    return true;
}

export async function validateMetadataFileStrictDetailed(file) {
    const errors = [];

    if (!file) {
        return { ok: false, errors: ["No metadata file provided"] };
    }

    let csvText = "";
    let header = [];
    let rows = [];

    try {
        csvText = await convertToCSV(file);
        ({ header, rows } = parseCSV(csvText));
    } catch (error) {
        return {
            ok: false,
            errors: [
                error?.message || "Could not read or convert metadata file",
            ],
        };
    }

    if (!header || header.length === 0 || isRowCompletelyEmpty(header)) {
        return {
            ok: false,
            errors: ["Metadata file is empty or missing header row"],
        };
    }

    if (!headerMatchesStrictWithEmptyExtras(header, rows)) {
        return {
            ok: false,
            errors: [
                `Header must match exactly: ${EXPECTED_HEADER.join(",")}`,
                "No extra named columns are allowed.",
            ],
        };
    }

    const idxSampleID = EXPECTED_HEADER.indexOf("sampleID");
    const idxCollectionDate = EXPECTED_HEADER.indexOf("collectionDate");
    const idxAuthors = EXPECTED_HEADER.indexOf("authors");

    let foundData = false;

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const r = rows[rowIndex];
        const line = rowIndex + 2;

        if (isRowCompletelyEmpty(r)) continue;

        if (isInstructionRow(r)) break;

        const sampleIDValue = r[idxSampleID];
        const dateValue = r[idxCollectionDate];
        const authorsValue = r[idxAuthors];

        foundData = true;

        if (!isValidSampleID(sampleIDValue)) {
            errors.push(
                `Row ${line}: sampleID must contain only letters, numbers, underscores (_) or hyphens (-)`
            );
        }

        if (!isEmptyCell(dateValue) && !isValidDateDDMMYY(dateValue)) {
            errors.push(
                `Row ${line}: collectionDate must use dd/mm/yyyy (example: 23/08/2020)`
            );
        }

        if (!isValidAuthors(authorsValue)) {
            errors.push(
                `Row ${line}: authors must contain names separated by semicolons`
            );
        }
    }

    if (!foundData) {
        errors.push("Metadata file has header but no valid data rows");
    }

    return { ok: errors.length === 0, errors };
}

export async function validateMetadataFileStrict(file) {
    const r = await validateMetadataFileStrictDetailed(file);
    return r.ok;
}